import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const CURRENCIES = [
  { code: 'USD', label: '달러' },
  { code: 'EUR', label: '유로' },
  { code: 'JPY', label: '일본' },
  { code: 'CNY', label: '중국' },
  { code: 'VND', label: '베트남' },
  { code: 'SGD', label: '싱가포르' },
  { code: 'AUD', label: '호주' },
  { code: 'CAD', label: '캐나다' },
];

const CACHE_KEY = 'trp_fx_cache_v1';
const SELECTED_KEY = 'trp_fx_selected_currency_v1';
const CACHE_TTL_MS = 60 * 1000;

const buildForexUrl = (fromCurrency) => {
  const base = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies';
  const from = String(fromCurrency || '').trim().toLowerCase();
  if (!from) return '';
  return `${base}/${encodeURIComponent(from)}.json`;
};

export default function ExchangeRateWidget({ compact = false }) {
  const [rates, setRates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [selectedCode, setSelectedCode] = useState('USD');
  const refreshTimerRef = useRef(null);

  const currencyList = useMemo(() => CURRENCIES, []);

  const fetchOne = useCallback(async (currencyCode, signal) => {
    const url = buildForexUrl(currencyCode);
    if (!url) throw new Error('지원하지 않는 통화입니다.');
    const res = await fetch(url, { signal, cache: 'no-store' });

    if (!res.ok) throw new Error('환율 정보를 불러오지 못했습니다.');

    const payload = await res.json().catch(() => null);
    const fromKey = String(currencyCode || '').trim().toLowerCase();
    const raw = payload && payload[fromKey] ? payload[fromKey].krw : null;
    const price = raw != null ? Number(raw) : null;
    const rounded = price != null && Number.isFinite(price) ? Math.round(price * 10) / 10 : null;
    const toValue = rounded != null && Number.isFinite(rounded) ? rounded.toLocaleString('ko-KR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '';

    return {
      code: currencyCode,
      toValue,
      toUnit: '원',
    };
  }, []);

  const loadRates = useCallback(async ({ force } = {}) => {
    const now = Date.now();

    if (!force) {
      try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached && cached.ts && cached.data && now - cached.ts < CACHE_TTL_MS) {
            setRates(cached.data);
            setUpdatedAt(cached.ts);
            setError('');
            return;
          }
        }
      } catch (_) {
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    setIsLoading(true);
    setError('');

    try {
      const results = await Promise.all(
        currencyList.map((c) => fetchOne(c.code, controller.signal))
      );

      const next = {};
      results.forEach((r) => {
        next[r.code] = { value: r.toValue, unit: r.toUnit };
      });

      setRates(next);
      setUpdatedAt(now);

      try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: now, data: next }));
      } catch (_) {
      }
    } catch (e) {
      if (e && e.name === 'AbortError') {
        setError('환율 정보를 불러오는데 시간이 오래 걸립니다. 잠시 후 다시 시도해 주세요.');
      } else {
        setError(e && e.message ? e.message : '환율 정보를 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  }, [currencyList, fetchOne]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(SELECTED_KEY);
      if (saved && currencyList.some((c) => c.code === saved)) {
        setSelectedCode(saved);
      }
    } catch (_) {
    }

    loadRates({ force: false });

    refreshTimerRef.current = setInterval(() => {
      loadRates({ force: false });
    }, 5 * 60 * 1000);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [loadRates]);

  const selectedCurrency = useMemo(
    () => currencyList.find((c) => c.code === selectedCode) || currencyList[0] || null,
    [currencyList, selectedCode]
  );

  const selectedRate = useMemo(() => {
    if (!selectedCurrency) return null;
    return rates[selectedCurrency.code] || null;
  }, [rates, selectedCurrency]);

  const handleChangeCurrency = useCallback((e) => {
    const next = e && e.target ? String(e.target.value || '') : '';
    if (!next) return;
    setSelectedCode(next);
    try {
      window.localStorage.setItem(SELECTED_KEY, next);
    } catch (_) {
    }
  }, []);

  const updatedLabel = useMemo(() => {
    if (!updatedAt) return '';
    try {
      const d = new Date(updatedAt);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    } catch (_) {
      return '';
    }
  }, [updatedAt]);

  const containerClassName = compact
    ? 'mt-1 rounded-lg border border-white/10 bg-white/5 px-2 py-2'
    : 'mt-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2';

  return (
    <div className={containerClassName}>
     

      <div className={compact ? 'mt-0' : 'mt-0'}>
        <select
          value={selectedCode}
          onChange={handleChangeCurrency}
          className={
            compact
              ? 'w-full bg-transparent text-slate-200 text-[10px] text-center outline-none truncate'
              : 'w-full bg-transparent text-slate-200 text-[11px] outline-none'
          }
          disabled={isLoading}
        >
          {currencyList.map((c) => (
            <option key={c.code} value={c.code} className="text-slate-900">
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className={compact ? 'mt-2 text-[10px] text-red-200 leading-snug' : 'mt-2 text-[11px] text-red-200 leading-snug'}>{error}</div>
      ) : (
        <div className={compact ? 'mt-2 text-center' : 'mt-2 text-center'}>
         
          <div className={compact ? 'mt-1 text-[12px] font-semibold text-slate-100 tabular-nums leading-none' : 'mt-1 text-[13px] font-semibold text-slate-100 tabular-nums'}>
            {(() => {
              const value = selectedRate?.value;
              
              if (value) return `${value}`;
              return isLoading ? '...' : '-';
            })()}
          </div>
        </div>
      )}

      {updatedLabel && !error && !compact && (
        <div className="mt-1 text-[10px] text-slate-400">업데이트 {updatedLabel}</div>
      )}
    </div>
  );
}
